import "./AppIcon.js";
import "./NavItems/NavLink.js";
import "./NavItems/PlatformSection.js";
import { updateWhenLocaleChanges } from "@lit/localize";
import { css, html, LitElement } from "lit";
import { map } from "lit/directives/map.js";
import { when } from "lit/directives/when.js";
import { APP_SECTIONS, LINKS, NAV_SECTIONS, OPEN_SECTION_EVENT_NAME, SETTINGS_ITEMS, } from "../configuration/constants.js";
import { defineComponent, filterAppNavigation } from "../configuration/utils.js";
import { navVar, transition } from "./styles.js";
const APP_SECTION_VALUES = Object.values(APP_SECTIONS);
const SETTINGS_SECTION_VALUES = Object.values(SETTINGS_ITEMS);
const DISABLED_LINKS = ["toolkits", "community", "documentation"];
const OPEN_IN_NEW_TAB = [SETTINGS_ITEMS.GOVMAPS.key];
const listVar = (name) => navVar(`list-${name}`);
const listDividerVar = (name) => navVar(`list-divider-${name}`);
/**
 * The `atlas-gn-app-switcher` component is used to render the app switcher menu.
 * It is used in the global nav component.
 *
 * @tag atlas-gn-app-switcher
 * @fires atlas-gn:open-section - Fired when a section is opened.
 * @prop {App[]} apps - The list of apps to render in the app switcher.
 * @prop {Links} links - The link configuration object.
 * @prop {AppKey | LinkKey} appId - The id of the current app.
 * @prop {OrgSettingsLinks} settings - The settings link configuration object.
 * @prop {boolean} hideSettings - Decides whether to show settings.
 * @prop {boolean} enableAppNav - Whether to enable app level navigation.
 */
export class AppSwitcher extends LitElement {
    constructor() {
        super();
        /* Render functions */
        this.renderApp = (item) => {
            const app = this.apps.find((app) => app.key === item.key);
            if (!app) {
                return html ``;
            }
            if (this._navLevel === "app" && this.appKey !== app.key) {
                return html ``;
            }
            return html `
      <li role="menuitem">
        ${when(this.appKey === app.key && this.enableAppNav, () => html `
            <atlas-gn-platform-section
              label=${app.title}
              ?is-highlighted=${this._navLevel === "platform"}
              ?is-open=${this._navLevel === "app"}
              @atlas-gn:open=${() => {
                this._navLevel = "app";
            }}
              @atlas-gn:close=${() => {
                this._navLevel = "platform";
            }}
            >
              <atlas-gn-app-icon slot="icon" app-key=${app.key}></atlas-gn-app-icon>
              <slot></slot>
            </atlas-gn-platform-section>
          `, () => html `
            <atlas-gn-nav-link
              label=${app.title}
              url=${app.url}
              show-expand-icon
              ?is-current=${this.appKey === app.key && this.enableAppNav === false}
              ?is-highlighted=${this.appKey === app.key && this.enableAppNav === true}
              ?is-disabled=${app.disabled == true}
            >
              <atlas-gn-app-icon slot="icon" app-key=${app.key}></atlas-gn-app-icon>
            </atlas-gn-nav-link>
          `)}
      </li>
    `;
        };
        this.renderSectionItem = (item, children, hasCurrentApp = false) => {
            // Note: this leads to an async import.
            // The browser is smart enough to not load the icon multiple times.
            item.loadIcon();
            const isActive = this._section === item.key;
            const isOtherSectionActive = this._section !== item.key && this._section !== NAV_SECTIONS.HOME.key;
            const hasChildren = children.values.length > 0;
            if (isOtherSectionActive || !hasChildren) {
                return;
            }
            // As the label might contain HTML (like the "&" character in section names),
            // we need to unwrap it to get the text, so we can bind it to the aria-label attribute.
            const label = item.label();
            const unwrappedLabel = typeof label === "object" ? label.strings[0] : label;
            return html `
      <li>
        <atlas-gn-platform-section
          label=${unwrappedLabel}
          ?is-open=${isActive}
          ?is-highlighted=${hasCurrentApp && (this.enableAppNav ? !isActive : true)}
          ?hide-summary=${this._navLevel === "app" && hasCurrentApp}
          @atlas-gn:close=${() => {
                this.backToHome();
            }}
          @atlas-gn:open=${() => {
                this.openSection(item.key);
            }}
        >
          <span slot="icon" aria-hidden="true">${item.icon()}</span>
          ${children}
        </atlas-gn-platform-section>
      </li>
    `;
        };
        this.renderLink = (item, openInNewTab = false) => {
            const href = this.links?.[item.key];
            if (!href) {
                return;
            }
            item.loadIcon();
            // its a boards experience customer if the app object contains disabled keyword.
            // used to disable the certain settings links if they are boards customer.
            const isDisabled = this.apps.some((item) => item.disabled == true) && DISABLED_LINKS.includes(item.key);
            return html `
      <li>
        <atlas-gn-nav-link
          label=${item.label()}
          url=${href}
          ?is-current=${this.appKey === item.key}
          ?is-disabled=${isDisabled}
          target=${openInNewTab ? "_blank" : "_self"}
        >
          <span slot="icon" aria-hidden="true">${item.icon()}</span>
        </atlas-gn-nav-link>
      </li>
    `;
        };
        this.renderSettings = (item) => {
            const href = this.settings?.[item.key];
            if (!href) {
                return;
            }
            item.loadIcon();
            const linkTarget = OPEN_IN_NEW_TAB.includes(item.key) ? "_blank" : "_self";
            return html `
      <li>
        <atlas-gn-nav-link label=${item.label()} url=${href} target=${linkTarget}>
          <span slot="icon" aria-hidden="true">${item.icon()}</span>
        </atlas-gn-nav-link>
      </li>
    `;
        };
        this.renderDivider = () => {
            return html `<li><hr class="transition" /></li>`;
        };
        this.onlyInSection = (navItem, contents) => {
            const isOpen = this._section === navItem.key;
            if (isOpen) {
                return contents;
            }
        };
        this.apps = [];
        this.links = {};
        this.appKey = undefined;
        this.settings = undefined;
        this._section = NAV_SECTIONS.HOME.key;
        this._navLevel = "platform";
        this.hideSettings = false;
        updateWhenLocaleChanges(this);
    }
    willUpdate(_oldProperties) {
        // When the component has not updated yet,
        // and the appKey is set, we need to determine the section, so
        // that it's open by default when the component is rendered.
        if (!this.hasUpdated && !_oldProperties.get("appKey") && this.appKey) {
            if (this.enableAppNav && this.appKey !== "launchpad") {
                this._navLevel = "app";
            }
            const filteredNav = Object.entries(this.filteredNavigation);
            const section = filteredNav.find((section) => {
                const app = section[1].find((app) => app.key === this.appKey);
                return app !== undefined;
            });
            if (section) {
                this._section = section[0];
            }
        }
    }
    get filteredNavigation() {
        const appKeys = this.apps.map((app) => app.key);
        return filterAppNavigation(appKeys);
    }
    render() {
        const filteredNav = this.filteredNavigation;
        return html `
      <ul class="transition">
        ${this.onlyInSection(NAV_SECTIONS.HOME, this.renderLink(LINKS.LAUNCHPAD))}
        ${map(APP_SECTION_VALUES, (section) => this.renderAppSection(section, filteredNav))}
        ${this.onlyInSection(NAV_SECTIONS.HOME, this.renderDivider())}
        ${this.hideSettings
            ? ""
            : this.renderSectionItem(NAV_SECTIONS.SETTINGS, html `${map(SETTINGS_SECTION_VALUES, (item) => this.renderSettings(item))}`, false)}
        ${this.onlyInSection(NAV_SECTIONS.HOME, this.renderLink(LINKS.MARKETPLACE))}
        ${this.onlyInSection(NAV_SECTIONS.HOME, this.renderLink(LINKS.DOCUMENTATION, true))}
        ${this.onlyInSection(NAV_SECTIONS.HOME, this.renderLink(LINKS.RESOURCES_SUPPORT, true))}
        ${this.onlyInSection(NAV_SECTIONS.HOME, this.renderLink(LINKS.DILIGENT_CONNECT, true))}
      </ul>
    `;
    }
    renderAppSection(sectionItem, navigation) {
        const section = navigation[sectionItem.key];
        if (section) {
            const items = html `${map(section, (item) => this.renderApp(item))}`;
            const hasCurrentApp = section.some((item) => item.key === this.appKey);
            return this.renderSectionItem(sectionItem, items, hasCurrentApp);
        }
    }
    /* Event handlers */
    openSection(key) {
        this._section = key;
        this.dispatchOpenSectionEvent(key);
    }
    backToHome() {
        this._section = NAV_SECTIONS.HOME.key;
        this.dispatchOpenSectionEvent(NAV_SECTIONS.HOME.key);
    }
    dispatchOpenSectionEvent(key) {
        const event = new CustomEvent(OPEN_SECTION_EVENT_NAME, {
            detail: key,
        });
        this.dispatchEvent(event);
    }
}
AppSwitcher.properties = {
    apps: { type: Array, reflect: false, attribute: "apps" },
    links: { type: Object, reflect: false, attribute: "links" },
    appKey: { type: String, reflect: true, attribute: "app-key" },
    settings: { type: Object, reflect: false, attribute: "settings" },
    hideSettings: { type: Boolean, reflect: false, attribute: "hide-settings" },
    enableAppNav: { type: Boolean, reflect: false, attribute: "enable-app-nav" },
    _section: { state: true },
    _navLevel: { state: true },
};
AppSwitcher.styles = [
    transition,
    css `
      :host {
        display: contents;
      }

      :host([label-hidden]) {
        --label-display: none;
      }

      ul,
      ul li {
        display: flex;
        flex-direction: column;
        margin: 0;
        list-style: none;
      }

      ul {
        gap: ${navVar("list-gap")};
        padding: 0;
      }

      ul li {
        gap: inherit;
      }

      :host > ul {
        --padding-left: var(--list-padding-left, ${navVar("collapsed-list-padding-left")});
        --padding-right: var(--list-padding-right, ${navVar("collapsed-list-padding-right")});
        --inset: var(--atlas-side-nav-inset, 0px);

        width: calc(var(--atlas-side-nav-width) - var(--padding-left) - var(--padding-right));

        padding-top: calc(${listVar("padding-top")} - var(--inset));
        padding-block: calc(${listVar("padding-bottom")} - var(--inset));
        padding-right: calc(var(--padding-right) - var(--inset));
        padding-left: calc(var(--padding-left) - var(--inset));
        transition-property: padding;
      }

      hr,
      ::slotted(hr) {
        border: none;
        border-top-color: ${listDividerVar("border-color")};
        border-top-style: ${listDividerVar("border-style")};
        border-top-width: ${listDividerVar("border-width")};
        margin-top: ${listDividerVar("margin-top")};
        margin-bottom: ${listDividerVar("margin-bottom")};
        margin-left: calc(-1 * var(--list-padding-left) - var(--atlas-side-nav-inset));
        margin-right: calc(-1 * var(--list-padding-right) - var(--atlas-side-nav-inset));
        padding: 0;
        transition-property: margin;
      }
    `,
];
export const APP_SWITCHER_TAG = "atlas-gn-app-switcher";
defineComponent(APP_SWITCHER_TAG, AppSwitcher);
